/**
 * Shared calculator engine and layout scaffolding.
 *
 * Design goals:
 *   • Educational only — every calculator prints a medical disclaimer and
 *     redirects users to a licensed provider consultation.
 *   • ADA compliant — every field has a real <label htmlFor>, keyboard-only
 *     flow, aria-describedby for formula/help text, and 4.5:1 contrast.
 *   • Zero sensitive-value leakage to analytics. `emitEvent()` only records
 *     that a calculator was USED — never the inputs or results.
 *   • Fully mobile responsive.
 *
 * To add a new calculator, drop a config object into `/pages/calculators/`
 * and pass it to <CalculatorShell config={cfg} />.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../../components/Breadcrumbs";
import Disclaimer from "../../components/Disclaimer";
import { CallCard } from "../../components/PhoneCTA";
import { Calculator as CalcIcon, Info, RefreshCw } from "lucide-react";

const ORIGIN = "https://medicalpeptides.ai";

/**
 * Fire an analytics event WITHOUT leaking any input or result values.
 * We record only the calculator slug and completion status so product can
 * measure engagement without ever transmitting height, weight, or age.
 */
function emitEvent(slug, phase) {
  try {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "calculator_engagement",
        calculator: slug,
        phase, // "opened" | "computed" | "reset"
      });
    }
  } catch (_) { /* analytics never blocks the UI */ }
}

export default function CalculatorShell({ config }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(config.fields.map((f) => [f.id, f.default ?? ""]))
  );
  const [result, setResult] = useState(null);

  useEffect(() => { emitEvent(config.slug, "opened"); }, [config.slug]);

  const compute = () => {
    // Only convert `number` inputs; leave `select` values as strings.
    const isNumeric = (f) => (f.type || "number") === "number";
    const byId = Object.fromEntries(config.fields.map((f) => [f.id, f]));
    const parsed = Object.fromEntries(Object.entries(values).map(([k, v]) => {
      if (isNumeric(byId[k])) return [k, v === "" ? "" : Number(v)];
      return [k, v];
    }));
    const missing = config.fields.filter((f) => {
      if (f.required === false) return false;
      const v = parsed[f.id];
      if (isNumeric(f)) return v === "" || Number.isNaN(v);
      return v === "" || v == null;
    });
    if (missing.length) {
      setResult({ error: `Please fill in: ${missing.map((f) => f.label).join(", ")}` });
      return;
    }
    try {
      const r = config.compute(parsed);
      setResult(r);
      emitEvent(config.slug, "computed");
    } catch (e) {
      setResult({ error: e.message || "Unable to compute — please check your inputs." });
    }
  };

  const reset = () => {
    setValues(Object.fromEntries(config.fields.map((f) => [f.id, f.default ?? ""])));
    setResult(null);
    emitEvent(config.slug, "reset");
  };

  const canonical = `${ORIGIN}/calculators/${config.slug}`;

  const softwareApp = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: config.title,
    applicationCategory: "HealthApplication",
    operatingSystem: "Web",
    description: config.metaDescription,
    url: canonical,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
  const bc = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Calculators", item: `${ORIGIN}/calculators` },
      { "@type": "ListItem", position: 3, name: config.title, item: canonical },
    ],
  };

  return (
    <div data-testid={`calculator-${config.slug}`}>
      <Helmet>
        <title>{`${config.title} | Medical Peptides`}</title>
        <meta name="description" content={config.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Medical Peptides Powered by VidaVital Medical" />
        <meta property="og:title" content={`${config.title} | Medical Peptides`} />
        <meta property="og:description" content={config.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${ORIGIN}/social-preview.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${config.title} | Medical Peptides`} />
        <meta name="twitter:description" content={config.metaDescription} />
        <meta name="twitter:image" content={`${ORIGIN}/social-preview.jpg`} />
        <script type="application/ld+json">{JSON.stringify(softwareApp)}</script>
        <script type="application/ld+json">{JSON.stringify(bc)}</script>
      </Helmet>

      <section className="vv-hero">
        <div className="vv-container py-12">
          <Breadcrumbs items={[
            { name: "Calculators", path: "/calculators" },
            { name: config.title, path: `/calculators/${config.slug}` },
          ]} />
          <div className="vv-hero-eyebrow mb-2 flex items-center gap-2"><CalcIcon size={14} /> Calculator</div>
          <h1 className="font-display text-4xl md:text-5xl">{config.title}</h1>
          {config.subtitle && <p className="mt-3 text-stone-600 max-w-3xl">{config.subtitle}</p>}
        </div>
      </section>

      <section className="vv-container py-8 grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="min-w-0">
          <form
            className="vv-card p-6 space-y-4"
            onSubmit={(e) => { e.preventDefault(); compute(); }}
            noValidate
          >
            <fieldset className="space-y-4">
              <legend className="sr-only">{config.title} inputs</legend>
              <div className="grid sm:grid-cols-2 gap-4">
                {config.fields.map((f) => (
                  <div key={f.id}>
                    <label htmlFor={`calc-${f.id}`} className="block text-sm font-medium text-stone-800 mb-1">
                      {f.label} {f.unit && <span className="text-stone-400">({f.unit})</span>}
                    </label>
                    {f.type === "select" ? (
                      <select
                        id={`calc-${f.id}`}
                        data-testid={`calc-input-${f.id}`}
                        value={values[f.id]}
                        onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                        aria-describedby={f.help ? `calc-${f.id}-help` : undefined}
                        className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--vv-teal)] outline-none"
                      >
                        {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input
                        id={`calc-${f.id}`}
                        data-testid={`calc-input-${f.id}`}
                        type={f.type || "number"}
                        inputMode="decimal"
                        min={f.min} max={f.max} step={f.step || "any"}
                        placeholder={f.placeholder || ""}
                        value={values[f.id]}
                        onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                        aria-describedby={f.help ? `calc-${f.id}-help` : undefined}
                        aria-required={f.required !== false}
                        className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--vv-teal)] outline-none"
                      />
                    )}
                    {f.help && (
                      <p id={`calc-${f.id}-help`} className="text-xs text-stone-500 mt-1">{f.help}</p>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                data-testid="calc-compute-btn"
                className="vv-btn-primary"
              >
                Calculate
              </button>
              <button
                type="button"
                onClick={reset}
                data-testid="calc-reset-btn"
                className="vv-btn-outline inline-flex items-center gap-2"
              >
                <RefreshCw size={14} /> Reset
              </button>
            </div>
          </form>

          {result && (
            <div
              className="vv-card p-6 mt-5"
              role="status"
              aria-live="polite"
              data-testid="calc-result"
            >
              {result.error ? (
                <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-3">
                  {result.error}
                </div>
              ) : (
                <>
                  <div className="text-xs uppercase tracking-widest text-stone-500">Your result</div>
                  <div className="mt-1 font-display text-3xl" data-testid="calc-result-value">
                    {result.value}
                    {result.unit ? <span className="text-lg text-stone-400 ml-1">{result.unit}</span> : null}
                  </div>
                  {result.category && (
                    <div className="mt-2 text-sm text-stone-700" data-testid="calc-result-category">
                      Category: <span className="font-medium">{result.category}</span>
                    </div>
                  )}
                  {result.explanation && <p className="mt-3 text-sm text-stone-700 leading-relaxed">{result.explanation}</p>}
                  {result.range && (
                    <div className="mt-3 text-xs text-stone-500">
                      General reference range: {result.range}
                    </div>
                  )}
                  <div className="mt-4 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3">
                    This result is educational only. Individual assessment requires evaluation by a licensed clinician.
                    <Link to="/request-consultation" className="ml-1 underline">Schedule a consultation</Link>.
                  </div>
                </>
              )}
            </div>
          )}

          {/* Formula explanation */}
          <div className="vv-card p-6 mt-5">
            <div className="text-xs uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Info size={13} /> How this is calculated</div>
            <div className="mt-2 text-sm text-stone-700 leading-relaxed space-y-2" data-testid="calc-formula">
              {config.formula}
            </div>
          </div>

          <div className="mt-6"><Disclaimer /></div>
        </div>

        {/* Sidebar — related content + CTA */}
        <aside className="space-y-5">
          <CallCard />
          {config.relatedArticles?.length ? (
            <div className="vv-card p-5">
              <div className="text-xs uppercase tracking-widest text-stone-500">Related education</div>
              <ul className="mt-3 space-y-1">
                {config.relatedArticles.map((slug) => (
                  <li key={slug}>
                    <Link to={`/articles/${slug}`} className="text-sm text-[color:var(--vv-gold-deep)] hover:underline">
                      {slug.replace(/-/g, " ")}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {config.relatedGoals?.length ? (
            <div className="vv-card p-5">
              <div className="text-xs uppercase tracking-widest text-stone-500">Related health goals</div>
              <ul className="mt-3 space-y-1">
                {config.relatedGoals.map((slug) => (
                  <li key={slug}>
                    <Link to={`/conditions/${slug}`} className="text-sm text-[color:var(--vv-gold-deep)] hover:underline">
                      {slug.replace(/-/g, " ")}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </aside>
      </section>
    </div>
  );
}

/** Small utility used inside `compute()` bodies to produce a categorized
 *  BMI/ratio result without duplicating the render code. */
export function bandResult({ value, unit, bands, explanation }) {
  const category = bands.find((b) => value >= b.min && value < b.max)?.label || "—";
  return { value: value.toFixed(1), unit, category, explanation };
}
